import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Wallet, ArrowRight, Check } from "lucide-react";
import { api_endpoints } from "@/lib/api";
import { fmtCurrency, PACKAGES } from "@/lib/format";
import { PageHeader, Btn, Card, Eyebrow } from "@/components/app/ui";

const TIER_META = {
    starter: {
        tagline: "credits.testTheWaters",
        features: [
            ["credits.fullReports", { count: 5 }],
            ["credits.chromeExtension"],
            ["credits.compareCollections"],
        ],
    },
    standard: {
        tagline: "credits.mostPopular",
        features: [
            ["credits.fullReports", { count: 15 }],
            ["credits.chromeExtension"],
            ["credits.compareCollections"],
        ],
        highlight: true,
    },
    hunter: {
        tagline: "credits.seriousShopping",
        features: [
            ["credits.fullReports", { count: 40 }],
            ["credits.chromeExtension"],
            ["credits.compareCollections"],
        ],
    },
};

export default function Credits() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [credits, setCredits] = React.useState(null);
    const [buying, setBuying] = React.useState(null);

    React.useEffect(() => {
        api_endpoints.creditsBalance().then((r) => setCredits(r.credits)).catch(() => {});
    }, []);

    async function buy(packageId) {
        setBuying(packageId);
        try {
            navigate(`/app/checkout/${packageId}`);
        } finally {
            setBuying(null);
        }
    }

    return (
        <div>
            <PageHeader
                eyebrow={t("credits.eyebrow")}
                title={t("credits.title")}
            />

            <div className="p-6 md:p-10 space-y-8">
                {/* Balance card */}
                <Card className="p-8 bg-black text-white" testid="balance-card">
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <Eyebrow className="text-zinc-400">── {t("credits.currentBalance")}</Eyebrow>
                            <div className="font-display text-7xl leading-none mt-3">
                                {credits ?? "—"}
                            </div>
                            <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 mt-2">
                                {t("credits.available")}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Wallet size={36} strokeWidth={1.5} className="text-[#FFCC00]" />
                            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-300 leading-relaxed">
                                ── {t("credits.neverExpires")}
                                <br />── {t("credits.noSubscription")}
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Tiers */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {Object.entries(PACKAGES).map(([id, p]) => {
                        const meta = TIER_META[id];
                        const isHighlight = meta?.highlight;
                        return (
                            <div
                                key={id}
                                data-testid={`tier-${id}`}
                                className={`p-8 flex flex-col border-2 border-black shadow-brutal ${isHighlight ? "bg-black text-white" : "bg-white text-black"}`}
                            >
                                <div className="flex items-start justify-between">
                                    <span
                                        className={`font-mono text-[10px] uppercase tracking-[0.25em] border-2 px-3 py-1 ${isHighlight ? "border-white bg-[#FFCC00] text-black" : "border-black bg-white"}`}
                                    >
                                        {meta?.tagline ? t(meta.tagline) : null}
                                    </span>
                                    <span
                                        className={`font-mono text-[10px] tracking-[0.25em] uppercase ${isHighlight ? "text-zinc-400" : "text-zinc-500"}`}
                                    >
                                        {p.credits} credits
                                    </span>
                                </div>
                                <h3 className="mt-6 font-display text-3xl md:text-4xl uppercase tracking-tight">
                                    {id}
                                </h3>
                                <div className="mt-6 flex items-baseline gap-3">
                                    <span className="font-display text-5xl leading-none">
                                        {fmtCurrency(p.price, p.currency)}
                                    </span>
                                </div>
                                <div className={`mt-2 font-mono text-[11px] uppercase tracking-[0.2em] ${isHighlight ? "text-zinc-400" : "text-zinc-500"}`}>
                                    ≈ {fmtCurrency(p.per, p.currency)} per analysis
                                </div>
                                <ul className="mt-6 space-y-3 flex-1">
                                    {(meta?.features || []).map(([key, params]) => (
                                        <li key={key} className="flex items-start gap-3 text-sm">
                                            <Check size={14} className="mt-0.5 flex-shrink-0" />
                                            <span>{t(key, params)}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className={`mt-3 font-mono text-[10px] uppercase tracking-[0.15em] ${isHighlight ? "text-zinc-400" : "text-zinc-500"}`}>
                                    {t("credits.allFeaturesNote")}
                                </div>
                                <Btn
                                    variant={isHighlight ? "yellow" : "primary"}
                                    icon={ArrowRight}
                                    onClick={() => buy(id)}
                                    disabled={buying === id}
                                    testid={`buy-${id}`}
                                    className="mt-8 justify-center"
                                >
                                    {t("credits.buyPack")}
                                </Btn>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
